import { tagString, type InstanceRef, type SeriesRef } from '../dicomweb';
import { SR_SOP_PREFIX, sopClassOf } from '../study';

/**
 * Render type of a series (spec section 1.2): a cornerstone stack, or non-image content shown by
 * src/display (encapsulated PDF, video, structured report).
 */
export type DisplayKind = 'stack' | 'pdf' | 'video' | 'sr';

export const ENCAPSULATED_PDF_SOP = '1.2.840.10008.5.1.4.1.1.104.1';

/** Video Endoscopic / Microscopic / Photographic Image Storage. */
export const VIDEO_SOP_CLASSES = new Set([
  '1.2.840.10008.5.1.4.1.1.77.1.1.1',
  '1.2.840.10008.5.1.4.1.1.77.1.2.1',
  '1.2.840.10008.5.1.4.1.1.77.1.4.1',
]);

/** MPEG-4 AVC / H.264 transfer syntaxes a browser can play from an MP4 container. */
export const PLAYABLE_VIDEO_SYNTAXES = new Set([
  '1.2.840.10008.1.2.4.102',
  '1.2.840.10008.1.2.4.103',
  '1.2.840.10008.1.2.4.104',
  '1.2.840.10008.1.2.4.105',
  '1.2.840.10008.1.2.4.106',
]);

/** Every video transfer syntax (MPEG-2 .100/.101, H.264 .102–.106, HEVC .107/.108). */
export function isVideoSyntax(uid: string): boolean {
  const match = /^1\.2\.840\.10008\.1\.2\.4\.(\d+)$/.exec(uid);
  if (!match) return false;
  const n = Number(match[1]);
  return n >= 100 && n <= 108;
}

const TRANSFER_SYNTAX_TAG = '00020010';
const AVAILABLE_SYNTAX_TAG = '00083002';
/** Modalities that only ever hold reports / documents (parity mode has no SOP class to go by). */
const PDF_MODALITIES = new Set(['DOC']);
const SR_MODALITIES = new Set(['SR']);

/** Transfer syntax of an instance when its metadata carries one (gateways may add it; Orthanc does not). */
export function transferSyntaxOf(instance: InstanceRef): string {
  if (!instance.metadata) return '';
  return tagString(instance.metadata, TRANSFER_SYNTAX_TAG) || tagString(instance.metadata, AVAILABLE_SYNTAX_TAG);
}

/** What a series is shown as. Decided from its first instance's SOP class / transfer syntax, else its modality. */
export function displayKind(series: SeriesRef): DisplayKind {
  const first = series.instances[0];
  if (!first) return 'stack';
  const sop = sopClassOf(first);
  if (sop === ENCAPSULATED_PDF_SOP) return 'pdf';
  if (sop.startsWith(SR_SOP_PREFIX)) return 'sr';
  if (VIDEO_SOP_CLASSES.has(sop) || isVideoSyntax(transferSyntaxOf(first))) return 'video';
  if (sop) return 'stack';
  if (PDF_MODALITIES.has(series.modality)) return 'pdf';
  if (SR_MODALITIES.has(series.modality)) return 'sr';
  return 'stack';
}
